/**
 * 메인 패널 수정 창 (다각형 엔진)
 * - 크기: 이 조각에서 고정 → 나머지가 자동으로 메움 (메울 패널 없으면 추가)
 * - 양 끝: 조각 안 접합만 양수/음수 바꿀 수 있음 — 바꾸면 접합 전체가 뒤집혀 상대도 반대로 (코너·문 쪽 끝은 고정)
 * - 실시간 미리보기 (적용 = 확정, 취소 = 원래대로)
 */
import React, { useEffect, useState } from 'react';
import { PolyPanel, PolyEdits, PieceInfo, EndType } from '../engine/polyEngine';
import { FillSpec, defaultRule } from '../utils/segmentFill';
import { selectAllOnFocus, useDragOffset } from './dialogKit';

interface Props {
  panel: PolyPanel;
  piece: PieceInfo;
  ascending: boolean;           // 이 패널 흐름이 좌표 오름차순인가 (a가 왼/위 끝)
  edits: PolyEdits;             // 현재 확정된 편집값
  spec: FillSpec;
  previewError?: string | null; // 미리보기 결과 오류 (규칙 적용 실패 등)
  onPreview: (e: PolyEdits | null) => void;
  onApply: (e: PolyEdits) => void;
  onCancel: () => void;
  onOpenSegment: () => void;
  onEditPartition?: () => void; // 칸막이 패널이면 — 칸막이 전체(위치·방향) 창으로
}

const TYPE_NAME: Record<EndType, string> = { yangsu: '양수', eumsu: '음수', corner: '코너', none: '평평(문)' };

const PolyPanelDialog: React.FC<Props> = ({ panel, piece, ascending, edits, spec, previewError, onPreview, onApply, onCancel, onOpenSegment, onEditPartition }) => {
  const [size, setSize] = useState(String(panel.size));
  const [flips, setFlips] = useState<Record<string, boolean>>({});
  const drag = useDragOffset();
  const n = piece.sizes.length;
  const i = panel.ascIndex;
  const [loName, hiName] = piece.horiz ? ['왼쪽', '오른쪽'] : ['위', '아래'];
  const loType = ascending ? panel.startType : panel.endType;
  const hiType = ascending ? panel.endType : panel.startType;
  const loJoint = i > 0 ? `${piece.key}:j${i - 1}` : null;   // 왼/위 접합
  const hiJoint = i < n - 1 ? `${piece.key}:j${i}` : null;   // 오른/아래 접합

  const sizeNum = parseInt(size);
  const sizeErr = isNaN(sizeNum) ? '숫자를 입력하세요'
    : sizeNum > spec.max ? `최대 ${spec.max}mm — 제작 불가` : sizeNum < spec.min ? `최소 ${spec.min}mm` : null;

  // 후보 편집값: 크기 → 조각 규칙의 고정, 접합 → 뒤집기 토글
  const build = (): PolyEdits => {
    const rules = { ...(edits.rules ?? {}) };
    if (!sizeErr && sizeNum !== panel.size) {
      const base = rules[piece.key] ?? defaultRule(spec);
      rules[piece.key] = { ...base, locks: { ...base.locks, [i + 1]: sizeNum } };
    }
    const f = { ...(edits.flips ?? {}) };
    Object.keys(flips).forEach(k => { if (flips[k]) f[k] = !f[k]; });
    return { rules, flips: f };
  };
  useEffect(() => { onPreview(build()); }, [size, flips]); // eslint-disable-line react-hooks/exhaustive-deps

  const endRow = (name: string, type: EndType, joint: string | null) => {
    const shown: EndType = joint && flips[joint] ? (type === 'yangsu' ? 'eumsu' : 'yangsu') : type;
    const editable = !!joint && (type === 'yangsu' || type === 'eumsu');
    return (
      <div style={{ marginBottom: 10 }}>
        <label style={{ display: 'block', marginBottom: 4, fontSize: 12, fontWeight: 'bold', color: '#555' }}>{name} 끝</label>
        {editable ? (
          <div style={{ display: 'flex', gap: 6 }}>
            {(['yangsu', 'eumsu'] as const).map(t => (
              <button key={t} onClick={() => { if (shown !== t) setFlips(p => ({ ...p, [joint!]: !p[joint!] })); }}
                style={{ flex: 1, padding: '6px 4px', fontSize: 12, cursor: 'pointer', borderRadius: 6,
                  border: shown === t ? '2px solid #3182F6' : '1px solid #E5E8EB', background: shown === t ? '#E8F3FF' : '#fff',
                  fontWeight: shown === t ? 'bold' : 'normal' }}>
                {t === 'yangsu' ? '◁ 양수' : '▷ 음수'}
              </button>
            ))}
          </div>
        ) : (
          <div style={{ fontSize: 12, color: '#777', padding: '6px 8px', background: '#f5f5f5', borderRadius: 6 }}>
            {type === 'none' && onEditPartition ? '평평' : TYPE_NAME[shown]} — 고정 {type === 'corner' ? '(코너 칸 소유)' : type === 'none' ? (onEditPartition ? '(칸막이 끝 — 벽·칸막이 면에 맞댐)' : '(문·개구부)') : '(코너에 꽂히는 끝)'}
          </div>
        )}
        {editable && <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>바꾸면 맞닿은 패널 끝도 반대로 바뀜</div>}
      </div>
    );
  };

  const err = sizeErr ?? previewError ?? null;
  return (
    <div style={{ position: 'fixed', top: 90, right: 24, width: 340, zIndex: 1000, background: '#fff', borderRadius: 20, overflow: 'hidden',
      boxShadow: '0 12px 40px rgba(0,0,0,0.16)', transform: `translate(${drag.offset.x}px, ${drag.offset.y}px)` }}>
      <div onMouseDown={drag.onMouseDown} style={{ background: '#fff', color: '#191F28', padding: '16px 20px 8px', borderRadius: 0,
        cursor: 'move', userSelect: 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontWeight: 'bold', fontSize: 14 }}>{onEditPartition ? '칸막이 ' : ''}패널 수정 — {i + 1}/{n}번 ({piece.total}mm 조각)</span>
        <button onClick={onCancel} style={{ width: 32, height: 32, borderRadius: '50%', border: 'none', background: '#F2F4F6', color: '#4E5968', fontSize: 14, cursor: 'pointer', flexShrink: 0 }}>✕</button>
      </div>
      <div style={{ padding: 16 }}>
        <label style={{ display: 'block', marginBottom: 4, fontSize: 12, fontWeight: 'bold', color: '#555' }}>패널 사이즈 (mm)</label>
        <input type="number" value={size} onChange={e => setSize(e.target.value)} onFocus={selectAllOnFocus} autoFocus
          min={spec.min} max={spec.max} step={10}
          style={{ width: '100%', padding: '6px 10px', fontSize: 13, borderRadius: 8, boxSizing: 'border-box', border: `1px solid ${err ? '#F04452' : '#ddd'}` }} />
        <div style={{ fontSize: 11, marginTop: 3, marginBottom: 10, color: err ? '#F04452' : '#999', fontWeight: err ? 'bold' : 'normal' }}>
          {err ? `⚠ ${err}` : '바꾸면 이 조각의 나머지 패널이 자동으로 길이를 맞춤'}
        </div>
        {endRow(loName, loType, loJoint)}
        {endRow(hiName, hiType, hiJoint)}
        {onEditPartition && <button onClick={onEditPartition} style={{ width: '100%', height: 36, marginTop: 4, fontSize: 13, fontWeight: 600, border: 'none', borderRadius: 10,
          background: '#F2F4F6', color: '#333D4B', cursor: 'pointer' }}>┃━ 칸막이 위치 · 방향 바꾸기</button>}
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <button onClick={onOpenSegment} style={{ marginRight: 'auto', padding: '7px 12px', fontSize: 13, border: '1px solid #3182F6', borderRadius: 8,
            background: '#fff', color: '#3182F6', cursor: 'pointer', fontWeight: 'bold' }}>📐 구간 채우기</button>
          <button onClick={onCancel} style={{ padding: '7px 16px', fontSize: 13, border: '1px solid #E5E8EB', borderRadius: 8, background: '#fff', cursor: 'pointer' }}>취소</button>
          <button onClick={() => onApply(build())} disabled={!!err}
            style={{ padding: '7px 16px', fontSize: 13, border: 'none', borderRadius: 8, background: '#3182F6', color: '#fff', fontWeight: 'bold',
              cursor: err ? 'not-allowed' : 'pointer', opacity: err ? 0.4 : 1 }}>적용</button>
        </div>
      </div>
    </div>
  );
};

export default PolyPanelDialog;
